import DefaultTheme from '@web-ui-kit/themes.default-theme'
import styled, { css } from 'styled-components'

import { TextVariant } from './text'

type CustomElementProps = {
  $bold: boolean
}

type TextProps = {
  $bold: boolean
  $variant: TextVariant
}

const commonStyles = css`
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  font-style: normal;
`

const commonHeaderStyles = css<CustomElementProps>`
  font-family: ${({ theme }) =>
    theme?.fonts?.heading ?? DefaultTheme.fonts.headline};
  font-weight: 300;
  line-height: 120%;
  ${({ $bold }) =>
    $bold &&
    css`
      font-weight: 700;
    `}
`

const commonBodyStyles = css<CustomElementProps>`
  font-family: ${({ theme }) => theme?.fonts?.body ?? DefaultTheme.fonts.body};
  font-weight: 300;
  line-height: 160%;

  ${({ $bold }) =>
    $bold &&
    css`
      font-weight: 700;
    `}
`

const title0Styles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 6rem;
  font-weight: 500;
`

const title0MobileStyles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 5rem;
  font-weight: 700;
`

const title1Styles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 4.3125rem;
`

const title1MobileStyles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 2.3125rem;
`

const title2Styles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 3.4375rem;
`

const title2MobileStyles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 2rem;
`

const title3Styles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 2.75rem;
`

const title3MobileStyles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 1.8125rem;
`

const title4Styles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 2.1875rem;
`

const title4MobileStyles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 1.5625rem;
`

const title5Styles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 1.75rem;
`

const title5MobileStyles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 1.4375rem;
`

const title6Styles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 1.375rem;
`

const title6MobileStyles = css<CustomElementProps>`
  ${commonHeaderStyles}
  font-size: 1.25rem;
`

const bodyStyles = css<CustomElementProps>`
  ${commonBodyStyles}
  font-size: 1.125rem;
`

const smallStyles = css<CustomElementProps>`
  ${commonBodyStyles}
  font-size: 0.875rem;
`

const superSmallStyles = css<CustomElementProps>`
  ${commonBodyStyles}
  font-size: 0.75rem;
`

const linkStyles = css<CustomElementProps>`
  ${commonBodyStyles}
  font-size: 1.125rem;
  text-decoration: underline;
`

const linkSmallStyles = css<CustomElementProps>`
  ${commonBodyStyles}
  font-size: 0.875rem;
  text-decoration: underline;
`

const linkSuperSmallStyles = css<CustomElementProps>`
  ${commonBodyStyles}
  font-size: 0.75rem;
  text-decoration: underline;
`

const buttonStyles = css<CustomElementProps>`
  ${commonBodyStyles}
  font-weight: 400;
  font-size: 1.125rem;

  ${({ $bold }) =>
    $bold &&
    css`
      font-weight: 700;
    `}
`

const buttonSmallStyles = css<CustomElementProps>`
  ${commonBodyStyles}
  font-weight: 400;
  font-size: 0.875rem;

  ${({ $bold }) =>
    $bold &&
    css`
      font-weight: 700;
    `}
`

const variantStyles = {
  body: bodyStyles,
  button: buttonStyles,
  buttonSmall: buttonSmallStyles,
  link: linkStyles,
  linkSmall: linkSmallStyles,
  linkSuperSmall: linkSuperSmallStyles,
  small: smallStyles,
  superSmall: superSmallStyles,
  title0: title0Styles,
  title0Mobile: title0MobileStyles,
  title1: title1Styles,
  title1Mobile: title1MobileStyles,
  title2: title2Styles,
  title2Mobile: title2MobileStyles,
  title3: title3Styles,
  title3Mobile: title3MobileStyles,
  title4: title4Styles,
  title4Mobile: title4MobileStyles,
  title5: title5Styles,
  title5Mobile: title5MobileStyles,
  title6: title6Styles,
  title6Mobile: title6MobileStyles,
}

const getSpecificVariantStyles = ($variant: TextVariant) => {
  return variantStyles[$variant]
}

export const Text = styled.span<TextProps>`
  ${commonStyles}

  ${({ $variant }) => getSpecificVariantStyles($variant)}
`
